/**
 * SettingsContext - Persistent UI preferences
 * 
 * Manages app-wide settings with localStorage persistence.
 * Extensible: add new settings by extending DEFAULTS and the context value.
 */

/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useCallback, useMemo, useEffect, useLayoutEffect } from 'react';
import {
  getNextModelActivationOverrides,
  normalizeModelActivationOverrides,
  resolveModelActivation,
} from '../services/models/repository';
import {
  getModelRepositoryVersion,
  subscribeModelRepository,
} from '../services/models/modelRepositoryRuntime';
import {
  getNextAgentModelDefaultOverrides,
  sanitizeAgentModelDefaultOverrides,
} from '../services/models/agentModelDefaults';
import {
  DEFAULT_MORE_LIKE_THIS_MODEL_KEY,
  normalizeMoreLikeThisModelKey,
} from '../services/creative-autopilot/moreLikeThisRouting';
import {
  DEFAULT_AGENT_APPROVAL_PROFILE,
  normalizeAgentApprovalProfile,
} from '../services/agent/contract/agentApprovalConfig';
import {
  normalizeAddedContextConnectorIds,
  normalizeContextConnectorId,
  normalizeEnabledContextConnectorIds,
} from '../services/connectors/contextConnectorRegistry';
import {
  DEFAULT_SIDE_PANEL_PRIMARY_WIDTH,
  DEFAULT_SIDE_PANEL_SECONDARY_DOCK_WIDTH,
  DEFAULT_SIDE_PANEL_SECONDARY_RATIO,
  MAX_SIDE_PANEL_PANE_WIDTH,
  MAX_SIDE_PANEL_SECONDARY_RATIO,
  MIN_SIDE_PANEL_PANE_WIDTH,
  MIN_SIDE_PANEL_SECONDARY_RATIO,
  SIDE_PANEL_INLINE_SCROLL_MODES,
  SIDE_PANEL_SECONDARY_DOCK_MODES,
} from '../constants/sidePanelDock';
import {
  DEFAULT_SLIDER_PRESENTATION,
  normalizeSliderPresentation,
  SLIDER_PRESENTATION_ATTRIBUTE,
} from '../constants/sliderPresentation';
import { DEFAULT_SOURCE_PREVIEW_HEIGHT, MAX_SOURCE_PREVIEW_HEIGHT } from '../constants/sourcePreview';

const STORAGE_KEY = 'glowpop_settings';

/**
 * Persisted settings schema version. Bump ONLY together with a step in `migrateSettings`.
 *
 * v1 — implicit, written as an ABSENT field. `timelineDockHeight` was materialized to the
 *      dock's static maximum for every session, so "never dragged" and "deliberately that
 *      height" were the same stored value.
 * v2 — `timelineDockHeight: null` means AUTO (open at content height). Any number is a
 *      deliberate choice and is honoured verbatim, including the old default's value.
 */
const SETTINGS_SCHEMA_VERSION = 2;

/**
 * What v1 actually WROTE for a never-dragged dock.
 *
 * ⛔ A FROZEN HISTORICAL LITERAL. Do not replace it with `MAX_TIMELINE_DOCK_HEIGHT`.
 * That constant was 392 only because it omitted the two text-track slots; it is 488 now
 * that the omission is fixed. This marker must keep matching the bytes v1 left in
 * localStorage, so correcting the constant must never move it.
 */
const V1_MATERIALIZED_TIMELINE_DOCK_HEIGHT = 392;

/**
 * One-shot forward migration of a parsed settings blob.
 *
 * WHAT THE v1 -> v2 STEP ACTUALLY CHANGES ON SCREEN. Measured by running the v1 clamp and
 * the migrated v2 path over the same stored 392, per track count:
 *
 *   tracks   1     2     3     5     7  |   8     9
 *   v1     104   152   200   296   392  | 392   392
 *   v2     104   152   200   296   392  | 440   488
 *
 * Everything up to SEVEN rows is identical, including seven itself — at seven rows the
 * content height IS 392, so AUTO and the stored value are the same number. Only eight and
 * nine row timelines move, and they move because 392 was never a real ceiling: it was
 * MAX_TIMELINE_DOCK_HEIGHT computed with TEXT missing from MAX_TRACK_COUNT. Correcting that
 * sum is what lets those docks open at the height their content always wanted.
 *
 * SO THIS IS A CEILING CORRECTION, NOT A LOST PREFERENCE. On an 8-9 row timeline a stored
 * 392 could only ever have meant "never dragged" or "dragged as far as it would go", since
 * 440/488 were not reachable — no user could have chosen 392 *instead of* them. AUTO reads
 * both cases faithfully. Reversing it would be the real loss: mapping 392 to an explicit
 * 392 would give every timeline of seven rows or fewer a 392px dock in place of the content
 * height they actually had.
 */
function migrateSettings(parsed) {
  const storedVersion = Number.isInteger(parsed?.schemaVersion) ? parsed.schemaVersion : 1;
  if (storedVersion >= SETTINGS_SCHEMA_VERSION) return { parsed, migrated: false };

  const next = { ...parsed };
  // Per-step gate. Today the early return above already excludes v2 blobs, so this reads
  // as redundant — it is not: when v3 lands, that return becomes `>= 3` and THIS is what
  // stops the v2 step running against v2 data. Keep each step gated by its own version.
  if (storedVersion < 2 && next.timelineDockHeight === V1_MATERIALIZED_TIMELINE_DOCK_HEIGHT) {
    next.timelineDockHeight = null;
  }
  next.schemaVersion = SETTINGS_SCHEMA_VERSION;
  return { parsed: next, migrated: true };
}
const LEGACY_AGENT_APPROVAL_PROFILE_STORAGE_KEY_PREFIX = 'glowghost:agent-approval-profile:v1:';

// HMR-resilient context: Store on globalThis to survive hot reloads
const CONTEXT_KEY = '__GLOWPOP_SETTINGS_CONTEXT__';
if (!globalThis[CONTEXT_KEY]) {
  globalThis[CONTEXT_KEY] = createContext(null);
}
const SettingsContext = globalThis[CONTEXT_KEY];

// Default values for all settings
const DEFAULTS = {
  schemaVersion: SETTINGS_SCHEMA_VERSION,
  panelBehavior: 'single', // 'multi' | 'single'
  sidePanelSecondaryDockMode: SIDE_PANEL_SECONDARY_DOCK_MODES.INLINE, // 'inline' | 'detached'
  sidePanelInlineSecondaryRatio: DEFAULT_SIDE_PANEL_SECONDARY_RATIO,
  sidePanelInlineScrollMode: SIDE_PANEL_INLINE_SCROLL_MODES.SPLIT, // 'split' | 'unified'
  sidePanelPrimaryWidth: DEFAULT_SIDE_PANEL_PRIMARY_WIDTH,
  sourcePreviewHeight: DEFAULT_SOURCE_PREVIEW_HEIGHT,
  sidePanelSecondaryDockWidth: DEFAULT_SIDE_PANEL_SECONDARY_DOCK_WIDTH,
  // null = AUTO: the dock opens at its content height for whatever track count is live.
  // Only a deliberate drag stores a number.
  timelineDockHeight: null,
  // Collapse bypasses the height contract; it never rewrites the user's parked height.
  timelineDockCollapsed: false,
  // Scene3D owns a separate dock family but opens the same way the timeline does.
  scene3dDockHeight: null,
  scene3dDockCollapsed: false,
  sliderPresentation: DEFAULT_SLIDER_PRESENTATION,
  batchUpscaleModel: 'standard', // 'fast' | 'standard' | 'premium'
  batchRemoveBackgroundModel: 'standard', // 'fast' | 'standard' | 'premium'
  showUndoRedoControls: false,
  contextualGhostPromptsEnabled: true,
  graphAutoRunEnabled: true,
  graphAutoRunDebounceMs: 400,
  agentApprovalProfile: DEFAULT_AGENT_APPROVAL_PROFILE,
  moreLikeThisModelKey: DEFAULT_MORE_LIKE_THIS_MODEL_KEY,
  modelActivationOverrides: {},
  modelAgentDefaultOverrides: {},
  addedContextConnectorIds: [],
  enabledContextConnectorIds: [],
  // Accelerated canvas is the default; a project listed here falls back to standard.
  standardCanvasProjectIds: [],
};

const normalizeProjectIds = (projectIds) => (
  Array.isArray(projectIds)
    ? [...new Set(projectIds
      .filter((id) => typeof id === 'string' && id.trim())
      .map((id) => id.trim()))]
    : []
);

function normalizeSettings(settings = {}) {
  const input = settings && typeof settings === 'object' ? settings : {};
  const parsedGraphAutoRunDebounceMs = Number(input.graphAutoRunDebounceMs);
  const parsedSidePanelInlineSecondaryRatio = Number(input.sidePanelInlineSecondaryRatio);
  // Number(null) and Number('') are 0, which is finite and would clamp to the
  // floor instead of falling back. An absent width must reach the default.
  const parsePaneWidth = (raw) => (
    raw === null || raw === undefined || raw === '' ? Number.NaN : Number(raw)
  );
  const parsedSidePanelPrimaryWidth = parsePaneWidth(input.sidePanelPrimaryWidth);
  const parsedSourcePreviewHeight = parsePaneWidth(input.sourcePreviewHeight);
  const parsedSidePanelSecondaryDockWidth = parsePaneWidth(input.sidePanelSecondaryDockWidth);
  const parsedTimelineDockHeight = parsePaneWidth(input.timelineDockHeight);
  const parsedScene3DDockHeight = parsePaneWidth(input.scene3dDockHeight);
  const agentApprovalProfile = hasOwnSetting(input, 'agentApprovalProfile')
    ? normalizeAgentApprovalProfile(input.agentApprovalProfile)
    : DEFAULTS.agentApprovalProfile;

  const normalizedEnabledIds = normalizeEnabledContextConnectorIds(input.enabledContextConnectorIds);
  const normalizedAddedSource = hasOwnSetting(input, 'addedContextConnectorIds')
    ? input.addedContextConnectorIds
    : normalizedEnabledIds;
  const normalizedAddedIds = normalizeAddedContextConnectorIds(normalizedAddedSource);
  const addedIdSet = new Set(normalizedAddedIds);
  for (const enabledId of normalizedEnabledIds) {
    if (!addedIdSet.has(enabledId)) {
      addedIdSet.add(enabledId);
      normalizedAddedIds.push(enabledId);
    }
  }
  const enabledSubset = normalizedEnabledIds.filter((id) => addedIdSet.has(id));

  return {
    // Carried, not recomputed: a normalize that dropped this would rewrite every blob as
    // v1 and re-run the migration on the next load, against already-migrated data.
    schemaVersion: Number.isInteger(input.schemaVersion)
      ? input.schemaVersion
      : DEFAULTS.schemaVersion,
    sourcePreviewHeight: Number.isFinite(parsedSourcePreviewHeight)
      ? Math.max(0, Math.min(MAX_SOURCE_PREVIEW_HEIGHT, parsedSourcePreviewHeight))
      : DEFAULT_SOURCE_PREVIEW_HEIGHT,
    panelBehavior: typeof input.panelBehavior === 'string'
      ? input.panelBehavior
      : DEFAULTS.panelBehavior,
    sidePanelSecondaryDockMode: input.sidePanelSecondaryDockMode === SIDE_PANEL_SECONDARY_DOCK_MODES.DETACHED
      ? SIDE_PANEL_SECONDARY_DOCK_MODES.DETACHED
      : DEFAULTS.sidePanelSecondaryDockMode,
    sidePanelInlineSecondaryRatio: Number.isFinite(parsedSidePanelInlineSecondaryRatio)
      ? Math.max(
        MIN_SIDE_PANEL_SECONDARY_RATIO,
        Math.min(parsedSidePanelInlineSecondaryRatio, MAX_SIDE_PANEL_SECONDARY_RATIO),
      )
      : DEFAULTS.sidePanelInlineSecondaryRatio,
    sidePanelInlineScrollMode: input.sidePanelInlineScrollMode === SIDE_PANEL_INLINE_SCROLL_MODES.UNIFIED
      ? SIDE_PANEL_INLINE_SCROLL_MODES.UNIFIED
      : DEFAULTS.sidePanelInlineScrollMode,
    sidePanelPrimaryWidth: Number.isFinite(parsedSidePanelPrimaryWidth)
      ? Math.max(
        MIN_SIDE_PANEL_PANE_WIDTH,
        Math.min(parsedSidePanelPrimaryWidth, MAX_SIDE_PANEL_PANE_WIDTH),
      )
      : DEFAULTS.sidePanelPrimaryWidth,
    sidePanelSecondaryDockWidth: Number.isFinite(parsedSidePanelSecondaryDockWidth)
      ? Math.max(
        MIN_SIDE_PANEL_PANE_WIDTH,
        Math.min(parsedSidePanelSecondaryDockWidth, MAX_SIDE_PANEL_PANE_WIDTH),
      )
      : DEFAULTS.sidePanelSecondaryDockWidth,
    // null = AUTO (content height). Settings cannot see the live track count, so it stores
    // the user's chosen number verbatim and lets the drag and the CSS bridge bound it.
    // NO VALUE IS RESERVED as a sentinel here — reinterpreting a legacy default belongs to
    // `migrateSettings`, which is version-gated and runs once, so a deliberately chosen
    // height survives every reload no matter which number it happens to be.
    timelineDockHeight: Number.isFinite(parsedTimelineDockHeight)
      ? parsedTimelineDockHeight
      : null,
    timelineDockCollapsed: typeof input.timelineDockCollapsed === 'boolean'
      ? input.timelineDockCollapsed
      : DEFAULTS.timelineDockCollapsed,
    scene3dDockHeight: Number.isFinite(parsedScene3DDockHeight)
      ? parsedScene3DDockHeight
      : null,
    scene3dDockCollapsed: typeof input.scene3dDockCollapsed === 'boolean'
      ? input.scene3dDockCollapsed
      : DEFAULTS.scene3dDockCollapsed,
    sliderPresentation: normalizeSliderPresentation(input.sliderPresentation),
    batchUpscaleModel: typeof input.batchUpscaleModel === 'string'
      ? input.batchUpscaleModel
      : DEFAULTS.batchUpscaleModel,
    batchRemoveBackgroundModel: typeof input.batchRemoveBackgroundModel === 'string'
      ? input.batchRemoveBackgroundModel
      : DEFAULTS.batchRemoveBackgroundModel,
    showUndoRedoControls: typeof input.showUndoRedoControls === 'boolean'
      ? input.showUndoRedoControls
      : DEFAULTS.showUndoRedoControls,
    contextualGhostPromptsEnabled: typeof input.contextualGhostPromptsEnabled === 'boolean'
      ? input.contextualGhostPromptsEnabled
      : DEFAULTS.contextualGhostPromptsEnabled,
    graphAutoRunEnabled: typeof input.graphAutoRunEnabled === 'boolean'
      ? input.graphAutoRunEnabled
      : DEFAULTS.graphAutoRunEnabled,
    graphAutoRunDebounceMs: Number.isFinite(parsedGraphAutoRunDebounceMs)
      && parsedGraphAutoRunDebounceMs >= 0
      ? Math.round(parsedGraphAutoRunDebounceMs)
      : DEFAULTS.graphAutoRunDebounceMs,
    agentApprovalProfile,
    moreLikeThisModelKey: normalizeMoreLikeThisModelKey(input.moreLikeThisModelKey),
    modelActivationOverrides: normalizeModelActivationOverrides(input.modelActivationOverrides),
    modelAgentDefaultOverrides: sanitizeAgentModelDefaultOverrides(input.modelAgentDefaultOverrides),
    addedContextConnectorIds: normalizedAddedIds,
    enabledContextConnectorIds: enabledSubset,
    standardCanvasProjectIds: normalizeProjectIds(input.standardCanvasProjectIds),
  };
}

function hasOwnSetting(settings, key) {
  return Boolean(settings)
    && typeof settings === 'object'
    && Object.prototype.hasOwnProperty.call(settings, key);
}

function getLegacyAgentApprovalProfileKeys(storage) {
  if (!storage || typeof storage.length !== 'number' || typeof storage.key !== 'function') {
    return [];
  }

  const keys = [];
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (
      typeof key === 'string'
      && key.startsWith(LEGACY_AGENT_APPROVAL_PROFILE_STORAGE_KEY_PREFIX)
    ) {
      keys.push(key);
    }
  }
  return keys;
}

function readLegacyAgentApprovalProfile(storage) {
  const keys = getLegacyAgentApprovalProfileKeys(storage);
  let foundFast = false;

  for (const key of keys) {
    const rawValue = storage.getItem(key);
    if (rawValue === 'careful') {
      return 'careful';
    }
    if (rawValue === DEFAULT_AGENT_APPROVAL_PROFILE) {
      foundFast = true;
    }
  }

  return foundFast ? DEFAULT_AGENT_APPROVAL_PROFILE : null;
}

function removeLegacyAgentApprovalProfile(storage) {
  for (const key of getLegacyAgentApprovalProfileKeys(storage)) {
    storage.removeItem(key);
  }
}

/**
 * Load settings from localStorage, merging with defaults
 */
const loadSettings = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      // Migrate the RAW blob before normalizing: the migration keys on what the previous
      // schema literally wrote, and normalize would have already reinterpreted it.
      const { parsed, migrated } = migrateSettings(JSON.parse(stored));
      const legacyApprovalProfile = hasOwnSetting(parsed, 'agentApprovalProfile')
        ? null
        : readLegacyAgentApprovalProfile(localStorage);
      const nextSettings = normalizeSettings({
        ...parsed,
        ...(legacyApprovalProfile ? { agentApprovalProfile: legacyApprovalProfile } : {}),
      });
      // Persist the upgrade so the migration runs once, not on every load.
      const saved = (migrated || legacyApprovalProfile) ? saveSettings(nextSettings) : false;
      if (legacyApprovalProfile && saved) {
        removeLegacyAgentApprovalProfile(localStorage);
      }
      return nextSettings;
    }

    const legacyApprovalProfile = readLegacyAgentApprovalProfile(localStorage);
    if (legacyApprovalProfile) {
      const nextSettings = normalizeSettings({
        agentApprovalProfile: legacyApprovalProfile,
      });
      if (saveSettings(nextSettings)) {
        removeLegacyAgentApprovalProfile(localStorage);
      }
      return nextSettings;
    }
  } catch (e) {
    console.warn('Failed to load settings:', e);
  }
  return { ...DEFAULTS };
};

/**
 * Save settings to localStorage
 */
const saveSettings = (settings) => {
  try {
    const persistedSettings = { ...settings };
    // Keep newly introduced Scene3D defaults sparse so unrelated setting writes do not
    // materialize a new persisted shape. Non-default values remain explicit and therefore
    // round-trip independently from the timeline dock.
    if (persistedSettings.scene3dDockHeight === DEFAULTS.scene3dDockHeight) {
      delete persistedSettings.scene3dDockHeight;
    }
    if (persistedSettings.scene3dDockCollapsed === DEFAULTS.scene3dDockCollapsed) {
      delete persistedSettings.scene3dDockCollapsed;
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(persistedSettings));
    return true;
  } catch (e) {
    console.warn('Failed to save settings:', e);
  }
  return false;
};

export const SettingsProvider = ({ children }) => {
  const [settings, setSettingsState] = useState(() => loadSettings());
  const [repositoryVersion, setRepositoryVersion] = useState(() => getModelRepositoryVersion());

  useLayoutEffect(() => {
    const root = document.documentElement;
    root.setAttribute(SLIDER_PRESENTATION_ATTRIBUTE, settings.sliderPresentation);
    return () => root.removeAttribute(SLIDER_PRESENTATION_ATTRIBUTE);
  }, [settings.sliderPresentation]);

  useEffect(() => {
    const unsubscribe = subscribeModelRepository(() => {
      setRepositoryVersion(getModelRepositoryVersion());
    });
    return unsubscribe;
  }, []);

  const modelActivation = useMemo(
    () => resolveModelActivation(settings.modelActivationOverrides),
    // repositoryVersion is intentionally included so activation recomputes when the
    // effective model repository changes (e.g., catalog rows merging in).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [settings.modelActivationOverrides, repositoryVersion],
  );

  const updateSetting = useCallback((key, value) => {
    setSettingsState(prev => {
      const next = normalizeSettings({ ...prev, [key]: value });
      saveSettings(next);
      return next;
    });
  }, []);

  const setModelActivation = useCallback((modelId, enabled) => {
    setSettingsState(prev => {
      const next = normalizeSettings({
        ...prev,
        modelActivationOverrides: getNextModelActivationOverrides(
          prev.modelActivationOverrides,
          modelId,
          enabled,
        ),
      });
      saveSettings(next);
      return next;
    });
  }, []);

  const toggleModelActivation = useCallback((modelId) => {
    setSettingsState(prev => {
      const currentActivation = resolveModelActivation(prev.modelActivationOverrides);
      const next = normalizeSettings({
        ...prev,
        modelActivationOverrides: getNextModelActivationOverrides(
          prev.modelActivationOverrides,
          modelId,
          !currentActivation[modelId],
        ),
      });
      saveSettings(next);
      return next;
    });
  }, []);

  const resetModelActivation = useCallback(() => {
    updateSetting('modelActivationOverrides', {});
  }, [updateSetting]);

  const setAgentModelDefault = useCallback((role, modelId) => {
    setSettingsState(prev => {
      const next = normalizeSettings({
        ...prev,
        modelAgentDefaultOverrides: getNextAgentModelDefaultOverrides(
          prev.modelAgentDefaultOverrides,
          role,
          modelId,
        ),
      });
      saveSettings(next);
      return next;
    });
  }, []);

  const resetAgentModelDefault = useCallback((role) => {
    setSettingsState(prev => {
      const next = normalizeSettings({
        ...prev,
        modelAgentDefaultOverrides: getNextAgentModelDefaultOverrides(
          prev.modelAgentDefaultOverrides,
          role,
          null,
        ),
      });
      saveSettings(next);
      return next;
    });
  }, []);

  const resetAgentModelDefaults = useCallback(() => {
    updateSetting('modelAgentDefaultOverrides', {});
  }, [updateSetting]);

  const setContextConnectorEnabled = useCallback((connectorId, enabled) => {
    setSettingsState(prev => {
      const nextId = normalizeContextConnectorId(connectorId);
      if (!nextId) {
        return prev;
      }

      const currentEnabled = normalizeEnabledContextConnectorIds(prev.enabledContextConnectorIds);
      const currentAdded = normalizeAddedContextConnectorIds(prev.addedContextConnectorIds);

      let nextEnabled = currentEnabled;
      let nextAdded = currentAdded;

      if (enabled) {
        if (!currentAdded.includes(nextId)) {
          nextAdded = [...currentAdded, nextId];
        }
        if (!currentEnabled.includes(nextId)) {
          nextEnabled = [...currentEnabled, nextId];
        }
      } else {
        nextEnabled = currentEnabled.filter((id) => id !== nextId);
      }

      const next = normalizeSettings({
        ...prev,
        addedContextConnectorIds: nextAdded,
        enabledContextConnectorIds: nextEnabled,
      });
      saveSettings(next);
      return next;
    });
  }, []);

  const setContextConnectorAdded = useCallback((connectorId, added) => {
    setSettingsState(prev => {
      const nextId = normalizeContextConnectorId(connectorId);
      if (!nextId) {
        return prev;
      }

      const currentAdded = normalizeAddedContextConnectorIds(prev.addedContextConnectorIds);
      const currentEnabled = normalizeEnabledContextConnectorIds(prev.enabledContextConnectorIds);

      let nextAdded = currentAdded;
      let nextEnabled = currentEnabled;

      if (added) {
        if (!currentAdded.includes(nextId)) {
          nextAdded = [...currentAdded, nextId];
        }
      } else {
        nextAdded = currentAdded.filter((id) => id !== nextId);
        nextEnabled = currentEnabled.filter((id) => id !== nextId);
      }

      const next = normalizeSettings({
        ...prev,
        addedContextConnectorIds: nextAdded,
        enabledContextConnectorIds: nextEnabled,
      });
      saveSettings(next);
      return next;
    });
  }, []);

  const setProjectAcceleratedCanvas = useCallback((projectId, enabled) => {
    if (typeof projectId !== 'string' || !projectId.trim()) return;
    const normalizedProjectId = projectId.trim();

    setSettingsState(prev => {
      const currentIds = normalizeProjectIds(prev.standardCanvasProjectIds);
      const nextIds = enabled
        ? currentIds.filter((id) => id !== normalizedProjectId)
        : [...new Set([...currentIds, normalizedProjectId])];
      const next = normalizeSettings({ ...prev, standardCanvasProjectIds: nextIds });
      saveSettings(next);
      return next;
    });
  }, []);

  // Convenience accessors for current settings
  const value = {
    // Panel behavior: 'multi' (all stay open) or 'single' (accordion)
    panelBehavior: settings.panelBehavior,
    setPanelBehavior: (mode) => updateSetting('panelBehavior', mode),

    // Side panel secondary dock: workspace chrome for Layers now, reusable by other contextual panels later
    sidePanelSecondaryDockMode: settings.sidePanelSecondaryDockMode,
    setSidePanelSecondaryDockMode: (mode) => updateSetting('sidePanelSecondaryDockMode', mode),
    sidePanelInlineSecondaryRatio: settings.sidePanelInlineSecondaryRatio,
    setSidePanelInlineSecondaryRatio: (ratio) => updateSetting('sidePanelInlineSecondaryRatio', ratio),
    sidePanelPrimaryWidth: settings.sidePanelPrimaryWidth,
    sourcePreviewHeight: settings.sourcePreviewHeight,
    setSourcePreviewHeight: (height) => updateSetting('sourcePreviewHeight', height),
    setSidePanelPrimaryWidth: (width) => updateSetting('sidePanelPrimaryWidth', width),
    sidePanelSecondaryDockWidth: settings.sidePanelSecondaryDockWidth,
    setSidePanelSecondaryDockWidth: (width) => updateSetting('sidePanelSecondaryDockWidth', width),

    // Timeline dock height: how tall the timeline band is; rows derive their height from it
    timelineDockHeight: settings.timelineDockHeight,
    setTimelineDockHeight: (height) => updateSetting('timelineDockHeight', height),
    timelineDockCollapsed: settings.timelineDockCollapsed,
    setTimelineDockCollapsed: (collapsed) => updateSetting('timelineDockCollapsed', collapsed),
    // Scene3D dock geometry is independent from the timeline dock's persisted facts.
    scene3dDockHeight: settings.scene3dDockHeight,
    setScene3DDockHeight: (height) => updateSetting('scene3dDockHeight', height),
    scene3dDockCollapsed: settings.scene3dDockCollapsed,
    setScene3DDockCollapsed: (collapsed) => updateSetting('scene3dDockCollapsed', collapsed),
    sidePanelInlineScrollMode: settings.sidePanelInlineScrollMode,
    setSidePanelInlineScrollMode: (mode) => updateSetting('sidePanelInlineScrollMode', mode),

    // Slider presentation: app-wide visual density for the shared horizontal Slider primitive
    sliderPresentation: settings.sliderPresentation,
    setSliderPresentation: (presentation) => updateSetting('sliderPresentation', presentation),

    // Batch upscale model: 'fast' | 'standard' | 'premium'
    batchUpscaleModel: settings.batchUpscaleModel,
    setBatchUpscaleModel: (model) => updateSetting('batchUpscaleModel', model),
    
    // Batch remove background model: 'fast' | 'standard' | 'premium'
    batchRemoveBackgroundModel: settings.batchRemoveBackgroundModel,
    setBatchRemoveBackgroundModel: (model) => updateSetting('batchRemoveBackgroundModel', model),

    // Floating workspace chrome: visibility only; history behavior and shortcuts remain active
    showUndoRedoControls: settings.showUndoRedoControls,
    setShowUndoRedoControls: (visible) => updateSetting('showUndoRedoControls', visible),

    // Contextual Ghost Prompts: whether project-aware suggestions participate in Ghost Whisper
    contextualGhostPromptsEnabled: settings.contextualGhostPromptsEnabled,
    setContextualGhostPromptsEnabled: (enabled) => updateSetting('contextualGhostPromptsEnabled', enabled),

    // Graph auto-run: global preference for debounced downstream execution in node graphs
    graphAutoRunEnabled: settings.graphAutoRunEnabled,
    setGraphAutoRunEnabled: (enabled) => updateSetting('graphAutoRunEnabled', enabled),

    // Graph auto-run debounce: wait after the last node edit before auto-running
    graphAutoRunDebounceMs: settings.graphAutoRunDebounceMs,
    setGraphAutoRunDebounceMs: (value) => updateSetting('graphAutoRunDebounceMs', value),

    // Agent approval profile: lets low-risk agent actions continue while high-impact tools stay gated
    agentApprovalProfile: settings.agentApprovalProfile,
    setAgentApprovalProfile: (profile) => updateSetting('agentApprovalProfile', profile),

    // More Like This: default image edit model used for sibling variations
    moreLikeThisModelKey: settings.moreLikeThisModelKey,
    setMoreLikeThisModelKey: (modelKey) => updateSetting('moreLikeThisModelKey', modelKey),

    // Model repository activation: user-managed visibility across eligible app surfaces
    modelActivationOverrides: settings.modelActivationOverrides,
    modelActivation,
    setModelActivation,
    toggleModelActivation,
    resetModelActivation,
    modelAgentDefaultOverrides: settings.modelAgentDefaultOverrides,
    setAgentModelDefault,
    resetAgentModelDefault,
    resetAgentModelDefaults,

    // Context connectors: user-selected MCP context sources available to agent flows
    addedContextConnectorIds: settings.addedContextConnectorIds,
    enabledContextConnectorIds: settings.enabledContextConnectorIds,
    setContextConnectorAdded,
    setContextConnectorEnabled,

    // Accelerated canvas preview: browser-local opt-in scoped to a project
    standardCanvasProjectIds: settings.standardCanvasProjectIds,
    setProjectAcceleratedCanvas,
    
    // Generic setter for future settings
    updateSetting
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

export const useOptionalSettings = () => useContext(SettingsContext);

export default SettingsContext;
